import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { Storage } from 'react-jhipster';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IEcole, defaultValue } from 'app/shared/model/ecole.model';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IEcole>,
  entity: defaultValue,
  entityDetails: defaultValue,
  logo: null as string | null,
  logoEcoleId: null as string | null,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/ecoles';

// Actions

export const getEcoles = createAsyncThunk('ecole/fetch_ecole_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}?cacheBuster=${new Date().getTime()}`;
  return axios.get<IEcole[]>(requestUrl);
});

export const getEcolesComptes = createAsyncThunk('ecole/fetch_ecole_compte', async () => {
  const requestUrl = `${apiUrl}/comptes`;
  return axios.get<IEcole[]>(requestUrl);
});

export const getEcole = createAsyncThunk(
  'ecole/fetch_ecole',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IEcole>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getLogo = createAsyncThunk(
  'ecole/fetch_logo',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}/logo`;
    try {
      const result = await axios.get<ArrayBuffer>(requestUrl, { responseType: 'arraybuffer' });
      const contentType = result.headers['content-type'] || 'image/png';
      const binary = Array.from(new Uint8Array(result.data))
        .map(charCode => String.fromCharCode(charCode))
        .join('');
      const base64 = window.btoa(binary);
      return `data:${contentType};base64,${base64}`;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
  { serializeError: serializeAxiosError }
);

export const getEcoleWithDetails = createAsyncThunk(
  'ecole/fetch_ecole_with_details',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}/comptes`;
    thunkAPI.dispatch(getEcole(id));
    return axios.get<IEcole>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const uploadLogo = createAsyncThunk(
  'ecole/upload_logo',
  async (entity: { id: string | number; file: FormData }, thunkAPI) => {
    const result = await axios.post<IEcole>(`${apiUrl}/${entity.id}/logo`, entity.file, {
      headers: { 'content-type': 'multipart/form-data' },
    });
    thunkAPI.dispatch(getEcole(entity.id));
    thunkAPI.dispatch(getLogo(entity.id));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteLogo = createAsyncThunk(
  'ecole/delete_logo',
  async (id: string | number, thunkAPI) => {
    const result = await axios.delete<IEcole>(`${apiUrl}/${id}/logo`);
    thunkAPI.dispatch(getEcole(id));
    thunkAPI.dispatch(getLogo(id));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const createEcole = createAsyncThunk(
  'ecole/create_ecole',
  async (entity: IEcole, thunkAPI) => {
    const result = await axios.post<IEcole>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEcoles({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEcole = createAsyncThunk(
  'ecole/update_ecole',
  async (entity: IEcole, thunkAPI) => {
    const result = await axios.put<IEcole>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEcoles({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const resetSchool = createAsyncThunk(
  'ecole/reset_ecole',
  async (id: string | number, thunkAPI) => {
    const result = await axios.put<IEcole>(`${apiUrl}/${id}/reset`);
    thunkAPI.dispatch(getEcoles({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEcole = createAsyncThunk(
  'ecole/delete_ecole',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IEcole>(requestUrl);
    thunkAPI.dispatch(getEcoles({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice
export type EcoleState = Readonly<typeof initialState>;
export const EcoleSlice = createSlice({
  name: 'ecole',
  initialState: initialState as EcoleState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEcole.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getLogo.pending, (state, action) => {
        state.logoEcoleId = String(action.meta.arg);
        state.logo = null;
      })
      .addCase(uploadLogo.fulfilled, (state, action) => {
        state.entity = action.payload.data;
      })
      .addCase(deleteLogo.fulfilled, (state, action) => {
        state.entity = action.payload.data;
      })
      .addCase(getLogo.fulfilled, (state, action) => {
        if (state.logoEcoleId === String(action.meta.arg)) {
          state.logo = action.payload;
        }
      })
      .addCase(getEcoleWithDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.entityDetails = action.payload.data;
      })
      .addCase(deleteEcole.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        // state.entity = {};
      })
      .addMatcher(isFulfilled(getEcoles, getEcolesComptes), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(createEcole, updateEcole, resetSchool), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEcoles, getEcole, getEcolesComptes, getEcoleWithDetails), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEcole, updateEcole, resetSchool, deleteEcole), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isRejected(getLogo), (state, action) => {
        if (state.logoEcoleId === String(action.meta.arg)) {
          state.logo = null;
        }
      });
  },
});

export const { reset } = EcoleSlice.actions;

// Reducer
export default EcoleSlice.reducer;
